import React, { ReactNode } from 'react';
import { Box } from '@heycater/design-system';
import { Entry } from 'contentful';
import Head from 'next/head';

import { getContentfulImageUrl } from 'hub/helpers/contentful';
import { ITestimonialFields } from 'lib/types/generated/contentful';
import Testimonials from 'shared/components/Testimonials';
import MainLayout from 'shared/layouts/MainLayout';
import { getRating } from 'shared/services/trustedShops/handleRatings';
import { Ratings } from 'shared/services/trustedShops/model';
import CTAWithBenefits from 'static_pages/components/CTAWithBenefits';
import CertificateSection from 'static_pages/marketing_pages/components/CertificateSection';
import HowItWorksCTA from 'static_pages/marketing_pages/components/HowItWorksCTA';
import Hero from 'static_pages/marketing_pages/SEAPage/Hero';
import TrustedCustomers from 'static_pages/marketing_pages/SEAPage/TrustedCustomers';
import Container from 'system/components/Container';

type Props = {
  meta: {
    description: string;
    title: string;
  };
  hero:
    | ReactNode
    | {
        title: string;
        city: string;
      };
  testimonials?: Entry<ITestimonialFields>[];
  ratings: Ratings;
};

function SEAPage({ meta, ratings, hero, testimonials }: Props) {
  const renderHero = () => {
    if (!hero) {
      return null;
    }
    if (typeof hero === 'object' && 'title' in hero) {
      return (
        <Hero title={hero.title} rating={getRating(ratings)} city={hero.city} />
      );
    }
    return hero;
  };

  return (
    <MainLayout ratings={ratings} hideNavigationLinks>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
      </Head>

      {renderHero()}

      <Box mt={{ xs: 6, md: 9 }} mb={{ xs: 4, md: 9 }}>
        <CertificateSection containerSize={1152} />
      </Box>
      <Container size="md" pb={{ xs: 4, md: 9 }}>
        <HowItWorksCTA />
      </Container>
      {testimonials?.length ? (
        <>
          <Container mb={{ xs: 0, md: 5 }}>
            <Testimonials
              items={testimonials.map((testimonial) => ({
                imageSrc: getContentfulImageUrl(testimonial.fields.image),
                body: testimonial.fields.body,
                company: {
                  name: testimonial.fields.company,
                  image: testimonial.fields.companyLogo
                    ? {
                        src: getContentfulImageUrl(
                          testimonial.fields.companyLogo
                        ),
                        height:
                          testimonial.fields.companyLogo.fields.file.details
                            .image?.height,
                        width:
                          testimonial.fields.companyLogo.fields.file.details
                            .image?.width,
                      }
                    : undefined,
                },
                title: testimonial.fields.title,
                name: testimonial.fields.name,
              }))}
            />
          </Container>
          <Container mb={{ xs: 4, md: 9 }}>
            <TrustedCustomers />
          </Container>
        </>
      ) : null}

      <CTAWithBenefits containerSize="sm" />
    </MainLayout>
  );
}

export default SEAPage;
